/*
  Quick view
  ----------
  Renders a product's own template in the quick view drawer, instead of the single
  shared "modal" template.

  How it works
  - sections/main-product.liquid renders an inert <template id="quick-view-content">
    (snippets/product-quick-view.liquid) holding the quick view version of the product:
    blocks with "Show in quick view" ticked, and the "Media — Quick View" gallery.
  - When a quick view opens we fetch the product page, lift that template's content into
    the drawer and let it activate.
  - Inside the drawer the product info is a <quick-view-product-info>, a thin subclass of
    the theme's ProductInfo. Its element ids carry a "__qv" namespace so they never clash
    with a product page that is also on screen.

  Must load after theme.js (it extends ProductInfo and QuickView from there).
*/
(() => {
  if (typeof ProductInfo !== 'function' || typeof QuickView !== 'function') {
    console.warn('[quick-view] theme.js has to load before quick-view.js');
    return;
  }

  const TEMPLATE_ID = 'quick-view-content';

  class QuickViewProductInfo extends ProductInfo {
    // Element ids are namespaced ("<section id>__qv"), so every selector ProductInfo
    // builds from sectionId stays unique even with the product page in the document.
    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    // ...but the section is still fetched by its real id on a variant change.
    buildRequestUrlWithParams(url, optionValues) {
      const params = [`section_id=${this.getAttribute('data-fetch-section-id')}`];
      if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
      return `${url}?${params.join('&')}`;
    }

    // The re-rendered section holds the quick view markup inside <template>, which
    // getElementById can't see into. Hand the inherited update logic the template's
    // content instead, so price, gallery, inventory etc. update from the quick view
    // markup rather than the product page's.
    handleUpdateProductInfo() {
      const update = super.handleUpdateProductInfo();
      return (parsedHTML, requestUrl) => {
        const template = parsedHTML.getElementById(TEMPLATE_ID);
        update(template ? template.content : parsedHTML, requestUrl);
      };
    }
  }

  if (!customElements.get('quick-view-product-info')) {
    customElements.define('quick-view-product-info', QuickViewProductInfo);
  }

  // Kept as a fallback for any product whose page doesn't carry the quick view template.
  // Remove along with sections/main-product-modal.liquid and templates/product.modal.json.
  const legacyQuickview = QuickView.prototype.quickview;

  QuickView.prototype.quickview = function () {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.getAttribute('data-product-url').split('?')[0];

    this.quickviewAbortController?.abort();
    this.quickviewAbortController = new AbortController();
    const { signal } = this.quickviewAbortController;

    fetch(productUrl, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        // Same pause as the theme: lets the drawer's opening animation settle first.
        setTimeout(() => {
          if (signal.aborted) return;

          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const template = parsedHTML.getElementById(TEMPLATE_ID);

          if (!template) {
            legacyQuickview.call(this);
            return;
          }

          // setInnerHTML re-creates <script> tags so they run (innerHTML alone won't).
          this.setInnerHTML(drawerContent, template.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.getAttribute('data-product-url')
            }
          }));
        }, 200);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  };
})();
